/**
 * The site tree on the Locations page (F5.3-b): GET /locations/:id/tree is nested site → zone →
 * bay → spot with each node's own items. The page shows it as an indented list, so it is
 * flattened once here with depth, path and the items below each node.
 */
import type { LocationDeclaration, LocationRow } from '../api/store'

export interface TreeNode {
  row: LocationRow
  depth: number
  /** "Godown A › Bay 3 › S11" — tree nodes carry no path of their own. */
  path: string
  /** Ids from the site down to the parent. */
  ancestors: string[]
  /** Bags and samples here and in every place below. */
  itemsBelow: number
  childCount: number
}

export const PATH_SEPARATOR = ' › '

/** Depth-first, in the server's order (by code). */
export function flattenTree(root: LocationRow): TreeNode[] {
  const out: TreeNode[] = []
  const walk = (row: LocationRow, depth: number, ancestors: string[], parentPath: string): number => {
    const path = parentPath ? `${parentPath}${PATH_SEPARATOR}${row.name}` : row.name
    const node: TreeNode = { row, depth, path, ancestors, itemsBelow: row.itemsHere, childCount: row.children?.length ?? 0 }
    out.push(node)
    for (const child of row.children ?? []) node.itemsBelow += walk(child, depth + 1, [...ancestors, row.id], path)
    return node.itemsBelow
  }
  walk(root, 0, [], '')
  return out
}

const matches = (n: TreeNode, q: string) =>
  [n.row.name, n.row.code, n.row.labelPayload ?? ''].some((s) => s.toLowerCase().includes(q))

/** Nodes whose name, code or label contains `query`, with their ancestors so the tree still reads. */
export function filterTree(nodes: TreeNode[], query: string): TreeNode[] {
  const q = query.trim().toLowerCase()
  if (!q) return nodes
  const keep = new Set<string>()
  for (const n of nodes) {
    if (!matches(n, q)) continue
    keep.add(n.row.id)
    for (const a of n.ancestors) keep.add(a)
  }
  return nodes.filter((n) => keep.has(n.row.id))
}

/** Drop the descendants of collapsed nodes. */
export function visibleNodes(nodes: TreeNode[], collapsed: ReadonlySet<string>): TreeNode[] {
  if (collapsed.size === 0) return nodes
  return nodes.filter((n) => !n.ancestors.some((a) => collapsed.has(a)))
}

/** Levels a new place may have under a parent (uc-core levelAllowedUnder). */
export function childLevels(level: string): string[] {
  switch (level) {
    case 'SITE':
      return ['ZONE', 'BAY']
    case 'ZONE':
      return ['BAY']
    case 'BAY':
      return ['POSITION']
    default:
      return []
  }
}

const LEVEL_PREFIX: Record<string, string> = { ZONE: 'Z', BAY: 'B', POSITION: 'S' }

/** Next free code under a parent: GDA + BAY → "GDA-B4" when B1..B3 are taken. */
export function suggestChildCode(parentCode: string, level: string, siblingCodes: readonly string[]): string {
  const prefix = `${parentCode}-${LEVEL_PREFIX[level] ?? ''}`.toUpperCase()
  const taken = new Set(siblingCodes.map((c) => c.toUpperCase()))
  let n = 1
  while (taken.has(`${prefix}${n}`)) n++
  return `${prefix}${n}`
}

/**
 * Places that never hold brand bags, so never need a no-fumigant declaration: the conventional
 * (red tie) bay, the hold area, the sample store and the dock. Only storage bays do.
 */
export const ROLES_WITHOUT_DECLARATION: readonly string[] = ['CONVENTIONAL', 'HOLD_AREA', 'SAMPLE_STORE', 'DOCK']

export function bayNeedsDeclaration(role: string | null | undefined): boolean {
  return !role || !ROLES_WITHOUT_DECLARATION.includes(role)
}

export type BayState = 'NONE' | 'EXPIRED_OR_OLD_FORM' | 'NOT_CLEAN' | 'EXPIRING' | 'OK'

/**
 * A bay's declaration state, in uc-core's order (declaration-query outOfDate): none, not in force,
 * not clean, then expiring (only the server knows the warning window, so `expiring` comes from
 * the out-of-date list).
 */
export function bayState(declaration: LocationDeclaration | null, expiring: boolean): BayState {
  if (!declaration) return 'NONE'
  if (!declaration.inForce) return 'EXPIRED_OR_OLD_FORM'
  if (!declaration.clean) return 'NOT_CLEAN'
  return expiring ? 'EXPIRING' : 'OK'
}
